<template>
  <span class="ontime-help-wrap">
    <b-button
      size="sm"
      variant="success"
      title="About the Driver On-Time % report"
      aria-label="Help"
      @click="show_help = true"
    >
      <i class="fa fa-info-circle"></i>
    </b-button>

    <b-modal
      v-model="show_help"
      title="Driver On-Time %"
      size="lg"
      ok-only
      scrollable
    >
      <div class="ontime-help-body">
        <h4>What this page shows</h4>
        <p>
          For each driver, the share of their bookings that met each on-time gate in the selected
          date range. A gate is a simple pass/fail check against the planned time &mdash; unlike the
          &plusmn;30 minute accuracy window on the Actuals Stats page, being <b>early is always on
          time</b>. Only lateness past the threshold counts as a miss.
        </p>

        <hr/>

        <h4>The three gates</h4>
        <ul>
          <li>
            <b>On Time To Yard</b> &mdash; the driver went on duty (or was assigned to the booked
            vehicle, per the tenant's HOS setting) at or before the planned start time.
          </li>
          <li>
            <b>On Time To Depart Yard</b> &mdash; the vehicle's first GPS-confirmed departure
            happened at or before the planned start time.
          </li>
          <li>
            <b>On Time To Pickup</b> &mdash; the vehicle reached the first pickup stop at or before
            that stop's planned arrival time. This is the arrival at the pickup location itself,
            taken from the segment that ends there &mdash; not the first passenger-carrying
            segment's own arrival, which is the stop <i>after</i> pickup.
          </li>
        </ul>
        <p>
          Each gate has its own <b>threshold</b>, in minutes of allowed lateness. At the default
          <b>0m</b> the actual time must be at or before planned; at 15m, up to fifteen minutes late
          still passes. Changing a threshold, the date range, the base, or the type re-runs nothing
          on its own &mdash; click <b>Get Results</b> to requery.
        </p>

        <hr/>

        <h4>Which bookings are counted</h4>
        <ul>
          <li>
            <b>Successful actuals only</b> &mdash; bookings that failed actuals processing, were
            cancelled, or were skipped are left out, as are any without both an HOS start and
            finish time.
          </li>
          <li>
            <b>Trip day 1 only</b> &mdash; later days of a multi-day trip are excluded. They have no
            yard departure or first pickup of their own, so counting them would drag every gate
            down.
          </li>
          <li>
            <b>Relief assignments are excluded</b> &mdash; a relief vehicle or relief driver sent out
            to take over part of a trip is identified by its route description mentioning
            &ldquo;relief&rdquo; (for example <i>Relief Vehicle Outbound</i> or <i>Main Vehicle
            Relief Driver</i>). Like day 2+, these have no yard departure or first pickup of their
            own, so they aren't measured against the gates.
          </li>
          <li>
            <b>Filler middle days</b> of multi-day trips &mdash; placeholder segments with default
            7am&ndash;10pm times &mdash; are also excluded.
          </li>
        </ul>

        <hr/>

        <h4>Filters</h4>
        <ul>
          <li>
            <b>Base</b> &mdash; limits results to bookings starting from one yard. Tenants with more
            than fifteen yards must pick a base before running, since an unscoped query across
            every yard returns nothing.
          </li>
          <li>
            <b>Type</b> &mdash; limits results to one booking type. Charter bookings are stored as
            either &ldquo;charter&rdquo; or &ldquo;booking&rdquo; depending on the tenant; both are
            treated as the same type here, so picking <b>Charter</b> matches either.
          </li>
        </ul>
        <p>
          On fleets over {{ large_fleet_vehicles }} vehicles the report does not run automatically
          when the page opens &mdash; the range query is slow enough there that it waits for you to
          click <b>Get Results</b>.
        </p>

        <hr/>

        <h4>Reading the driver table</h4>
        <ul>
          <li>
            Each gate column shows the percentage plus the underlying counts, as
            <i>(on time / measured)</i>. The two can differ between gates: a booking with no GPS
            departure counts toward To Yard but not Depart Yard.
          </li>
          <li>
            <b>Bookings</b> is the driver's qualifying bookings after the exclusions above &mdash;
            so it can be lower than their total activity in the range.
          </li>
          <li>
            Drivers need at least <b>2</b> qualifying bookings to appear, so single one-off trips
            don't show up as 0% or 100%.
          </li>
          <li>
            Click a driver's name for the booking-by-booking detail behind their percentages,
            including each planned vs. actual time and the minutes early or late.
          </li>
          <li>
            Percentages are colored green at 90% and above and red below 70%.
          </li>
        </ul>

        <hr/>

        <h4>Download</h4>
        <p>
          <b>Download</b> exports the per-driver table as CSV using the same date range, base, type,
          and thresholds currently applied.
        </p>
      </div>
    </b-modal>
  </span>
</template>

<script>
export default {
  name: 'actuals_on_time_report_help',

  props: {
    // kept in sync with the page's LARGE_FLEET_VEHICLES so the help text can't
    // quote a threshold the page no longer uses
    largeFleetVehicles: {
      type: Number,
      default: 100,
    },
  },

  data() {
    return {
      show_help: false,
    }
  },

  computed: {
    large_fleet_vehicles() {
      return this.largeFleetVehicles
    },
  },
}
</script>

<style scoped>
.ontime-help-wrap {
  display: inline-block;
}
.ontime-help-body {
  font-size: 14px;
  text-align: left;
}
.ontime-help-body h4 {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 6px;
}
.ontime-help-body ul {
  padding-left: 20px;
  margin-bottom: 10px;
}
.ontime-help-body li {
  margin-bottom: 5px;
}
.ontime-help-body hr {
  margin: 14px 0;
}
</style>
